import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import * as L from 'leaflet';

interface MapContextValue {
  map: L.Map | null;
}

const MapContext = createContext<MapContextValue>({ map: null });

export function useMap(): L.Map {
  const { map } = useContext(MapContext);
  if (!map) {
    throw new Error('useMap must be used inside a MapContainer');
  }
  return map;
}

interface MapContainerProps {
  center: [number, number];
  zoom: number;
  className?: string;
  children?: ReactNode;
}

export function MapContainer({ center, zoom, className, children }: MapContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const instance = L.map(containerRef.current, {
      center,
      zoom,
      zoomControl: true,
      attributionControl: true,
    });

    setMap(instance);

    // Leaflet needs a layout pass to measure the container.
    const frame = requestAnimationFrame(() => instance.invalidateSize());

    return () => {
      cancelAnimationFrame(frame);
      instance.remove();
    };
  }, []);

  return (
    <div ref={containerRef} className={className} style={{ height: '100%', width: '100%' }}>
      {map && <MapContext.Provider value={{ map }}>{children}</MapContext.Provider>}
    </div>
  );
}

interface TileLayerProps {
  attribution: string;
  url: string;
}

export function TileLayer({ attribution, url }: TileLayerProps) {
  const { map } = useContext(MapContext);

  useEffect(() => {
    if (!map) return;

    const layer = L.tileLayer(url, { attribution }).addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, attribution, url]);

  return null;
}

interface PopupContextValue {
  marker: L.CircleMarker | null;
}

const PopupContext = createContext<PopupContextValue>({ marker: null });

interface CircleMarkerProps {
  center: [number, number];
  radius: number;
  pathOptions: L.PathOptions;
  children?: ReactNode;
}

export function CircleMarker({ center, radius, pathOptions, children }: CircleMarkerProps) {
  const { map } = useContext(MapContext);
  const [marker] = useState(() => L.circleMarker(center, { radius, ...pathOptions }));

  useEffect(() => {
    if (!map) return;
    marker.addTo(map);
    return () => {
      marker.remove();
    };
  }, [map, marker]);

  useEffect(() => {
    marker.setLatLng(center);
    marker.setRadius(radius);
    marker.setStyle(pathOptions);
  }, [center, marker, pathOptions, radius]);

  return <PopupContext.Provider value={{ marker }}>{children}</PopupContext.Provider>;
}

interface PopupProps {
  children?: ReactNode;
}

export function Popup({ children }: PopupProps) {
  const { marker } = useContext(PopupContext);
  const [container] = useState(() =>
    typeof document !== 'undefined' ? document.createElement('div') : null
  );

  useEffect(() => {
    if (!marker || !container) return;
    marker.bindPopup(container);
    return () => {
      marker.unbindPopup();
    };
  }, [marker, container]);

  // If the popup DOM changes while the popup is open, tell Leaflet to re-measure.
  useEffect(() => {
    if (!container) return;
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      const popupInstance = marker?.getPopup();
      if (popupInstance?.isOpen()) {
        popupInstance.update();
      }
    });

    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [container, marker]);

  if (!container || !children) return null;
  return createPortal(children, container);
}
